import type {
  BarSeriesOption,
  EChartsOption,
  LineSeriesOption,
  PictorialBarSeriesOption,
  XAXisComponentOption,
  YAXisComponentOption,
} from 'echarts';

import {
  BAR_BASE_OPACITY,
  LINE_FILL_OPACITY,
  SOLID_FILL_OPACITY,
} from 'components/Studio/chartStyle';

import {
  getSeriesIcon,
  toIconSymbol,
  toPictorialSeriesOption,
  toPictorialTrackOptions,
} from './pictorial';
import {
  FONT_SIZE,
  FormatMetric,
  formatAxisValue,
  formatValueLabel,
  toBaseOption,
  toGradient,
  toLegend,
  toTooltipHtml,
  toTooltipStyle,
  toValueLabelStyle,
} from './shared';

// Width set aside for each further value axis on the same side
const AXIS_OFFSET = 48;

// Roughly how wide a label is drawn, at the axis font size
const estimateLabelWidth = (label: string) => label.length * FONT_SIZE * 0.6;

// As Highcharts does: a category axis turns its labels when they don't fit
// side by side in the chart's width (unknown until it's laid out)
export const shouldRotateLabels = ({
  model,
  width,
}: {
  model: StudioCharts.CartesianModel;
  width: number;
}) => {
  if (model.isHorizontal || width <= 0 || model.categories.length === 0) {
    return false;
  }
  const step = model.axisLabelStep ?? 1;
  const shown = model.categories.filter((_c, index) => index % step === 0);
  const widest = Math.max(...shown.map((c) => estimateLabelWidth(c.label)));
  return widest * shown.length > width * 0.85;
};

// Room between two side-by-side axis labels
const LABEL_GAP = 16;
// Room each value axis's labels take beside the plot
const VALUE_AXIS_WIDTH = 40;

export interface AxisLabelLayout {
  isRotated: boolean;
  // Every step-th category is labelled; absent labels as many as fit
  step?: number;
}

// How the category axis labels the chart's width: dates thin out to every
// nth (as the Highcharts line and bar charts do), so they read flat; terms
// all keep a label, turned when they don't fit side by side
export const getAxisLabelLayout = ({
  model,
  width,
}: {
  model: StudioCharts.CartesianModel;
  width: number;
}): AxisLabelLayout => {
  const count = model.categories.length;
  if (model.isHorizontal || width <= 0 || count === 0) {
    return { isRotated: false, step: model.axisLabelStep };
  }
  if (model.isDateAxis) {
    const plotWidth = Math.max(
      1,
      width - VALUE_AXIS_WIDTH * Math.max(1, model.valueAxes.length),
    );
    const widest = Math.max(
      ...model.categories.map((c) => estimateLabelWidth(c.label)),
    );
    const fitStep = Math.ceil((count * (widest + LABEL_GAP)) / plotWidth);
    return {
      isRotated: false,
      step: Math.max(model.axisLabelStep ?? 1, fitStep, 1),
    };
  }
  return {
    isRotated: shouldRotateLabels({ model, width }),
    step: model.axisLabelStep,
  };
};

const toCategoryAxis = ({
  model,
  theme,
  labelLayout,
}: {
  model: StudioCharts.CartesianModel;
  theme: StudioCharts.Theme;
  labelLayout: AxisLabelLayout;
}) => ({
  type: 'category' as const,
  data: model.categories.map((c) => c.label),
  // Horizontal bars list their first category at the top
  inverse: model.isHorizontal,
  axisLine: { lineStyle: { color: theme.axisLine } },
  axisTick: { show: false },
  axisLabel: {
    color: theme.textPrimary,
    fontSize: FONT_SIZE,
    hideOverlap: true,
    rotate: labelLayout.isRotated ? 45 : 0,
    interval: labelLayout.step ? labelLayout.step - 1 : ('auto' as const),
  },
  splitLine: { show: false },
});

// Room a value axis leaves past its values (Highcharts' min/maxPadding)
const AXIS_PADDING_SHARE = 0.05;
const AXIS_PADDING = `${AXIS_PADDING_SHARE * 100}%`;

// Room each value axis interval needs, so a short chart isn't crowded: up
// the chart, as the Highcharts value axis spaces its ticks (tickPixelInterval)
const PX_PER_VERTICAL_TICK = 72;
const PX_PER_HORIZONTAL_TICK = 90;
const MIN_TICKS = 2;
const MAX_TICKS = 5;

// About how many intervals the value axis is split into for the room it
// has: its plot's height (or width, for horizontal bars). ECharts' own
// default (5) otherwise.
export const getValueTickCount = ({
  isHorizontal,
  width,
  height,
}: {
  isHorizontal: boolean;
  width: number;
  height: number;
}): number | undefined => {
  const length = isHorizontal ? width : height;
  if (length <= 0) {
    return undefined;
  }
  const count = Math.floor(
    length / (isHorizontal ? PX_PER_HORIZONTAL_TICK : PX_PER_VERTICAL_TICK),
  );
  return Math.max(MIN_TICKS, Math.min(MAX_TICKS, count));
};

// The id every stacked series shares, so each stacks on the one before
const STACK = 'stack';

// What a series draws: its values, or on a percentage stack, each as its
// share of its category's total (its values stay as they are for the
// tooltip)
const toDrawnValues = ({
  series,
  model,
}: {
  series: StudioCharts.Series;
  model: StudioCharts.CartesianModel;
}): (number | null)[] => {
  if (!model.stack || !model.stack.isPercentage) {
    return series.values;
  }
  return series.values.map((value, index) => {
    if (value === null) {
      return null;
    }
    const total = model.series.reduce(
      (sum, s) => sum + Math.abs(s.values[index] ?? 0),
      0,
    );
    return total === 0 ? 0 : (value / total) * 100;
  });
};

// Whether a drill-down greys a series' item: where it picked another
// category, or on a chart whose series are its terms, another series
const isDeselected = ({
  series,
  model,
  index,
}: {
  series: StudioCharts.Series;
  model: StudioCharts.CartesianModel;
  index: number;
}) => {
  const { selectedKeys } = model;
  if (!selectedKeys) {
    return false;
  }
  if (model.drillsOn === 'series') {
    return !selectedKeys.includes(series.key ?? '');
  }
  const category = model.categories[index];
  return !!category && !selectedKeys.includes(category.key);
};

// Room past an axis's values before it rounds to its next tick, as the
// Highcharts axis leaves (min/maxPadding), but never across zero: values
// that are all positive (or all negative) keep their axis on that side of
// it, as Highcharts' soft threshold does. Bars always reach zero. A
// percentage stack fills its axis to 100%. A set min or max is kept as it
// is.
const toBoundaryGap = ({
  model,
  axisIndex,
  isFitted,
}: {
  model: StudioCharts.CartesianModel;
  axisIndex: number;
  isFitted: boolean;
}): [string | number, string | number] => {
  if (model.stack && model.stack.isPercentage) {
    return [0, 0];
  }
  const values = model.series
    .filter((s) => s.axisIndex === axisIndex)
    .flatMap((s) => s.values)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (values.length === 0) {
    return [0, AXIS_PADDING];
  }
  const dataMin = values.reduce((a, b) => Math.min(a, b));
  const dataMax = values.reduce((a, b) => Math.max(a, b));
  const low = isFitted ? dataMin : Math.min(dataMin, 0);
  const high = isFitted ? dataMax : Math.max(dataMax, 0);
  const padding = (high - low) * AXIS_PADDING_SHARE;
  return [
    low < 0 || low - padding >= 0 ? AXIS_PADDING : 0,
    high > 0 || high + padding <= 0 ? AXIS_PADDING : 0,
  ];
};

// The max of an axis that spans at least its minimum range. Values that
// already span it leave the max to ECharts (undefined), so it rounds to a
// tick. Narrower ones stretch it to the range, rounded up to the range's
// order of magnitude: a set max is where an axis's ticks count from, so
// an unrounded one (10.120000000000001) leaves none of them round.
export const toMinRangeMax = ({
  min,
  max,
  minRange,
}: {
  min: number;
  max: number;
  minRange: number;
}): number | undefined => {
  if (max - min >= minRange) {
    return undefined;
  }
  const step = 10 ** Math.floor(Math.log10(minRange));
  // toPrecision drops float noise, which would otherwise round a max on a
  // step (0.13 + 0.17) up past it, or leave one unrounded (3 * 0.1)
  const steps = Math.ceil(Number(((min + minRange) / step).toPrecision(12)));
  return Number((steps * step).toPrecision(12));
};

const toValueAxes = ({
  model,
  theme,
  tickCount,
}: {
  model: StudioCharts.CartesianModel;
  theme: StudioCharts.Theme;
  tickCount?: number;
}) => {
  const [start, end] = model.isHorizontal
    ? (['bottom', 'top'] as const)
    : (['left', 'right'] as const);
  const countBySide = { start: 0, end: 0 };
  return model.valueAxes.map((axis, index) => {
    const side = axis.isOpposite ? 'end' : 'start';
    const offset = countBySide[side] * AXIS_OFFSET;
    countBySide[side] += 1;
    const { minRange } = axis;
    // A combo's line on an axis of its own fits that axis, whatever the
    // bars on the others do
    const hasBars = model.series.some(
      (s) => s.kind === 'bar' && s.axisIndex === index,
    );
    const isFitted = !hasBars && !model.stack;
    return {
      type: 'value' as const,
      // Bars and stacks measure from zero; lines fit their axis to their
      // values
      scale: isFitted,
      boundaryGap: toBoundaryGap({ model, axisIndex: index, isFitted }),
      // The first axis is split for the room it has; the others line their
      // ticks up with it, so the chart has one set of grid lines
      ...(index === 0
        ? tickCount !== undefined
          ? { splitNumber: tickCount }
          : {}
        : { alignTicks: true }),
      position: side === 'end' ? end : start,
      offset,
      min: axis.min,
      max:
        axis.max !== undefined
          ? axis.max
          : minRange !== undefined
            ? ({ min, max }: { min: number; max: number }) =>
                toMinRangeMax({ min, max, minRange })
            : undefined,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: theme.textMuted,
        fontSize: FONT_SIZE,
        formatter:
          model.stack && model.stack.isPercentage
            ? (value: number) => `${value}%`
            : formatAxisValue,
        hideOverlap: true,
      },
      // Only the first axis draws grid lines, so they don't cross
      splitLine: {
        show: index === 0,
        lineStyle: { color: theme.gridLine },
      },
    };
  });
};

const toBarSeriesOption = ({
  series,
  model,
  theme,
  formatMetric,
}: {
  series: StudioCharts.Series;
  model: StudioCharts.CartesianModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): BarSeriesOption => {
  const { dataLabels } = model;
  const step = dataLabels ? dataLabels.step : undefined;
  // Only the metric's own bars, not what's drawn from them
  const style = series.role === 'value' ? model.style ?? {} : {};
  // Stacked bars are rounded all round, as the Highcharts ones; others only
  // at their tip
  const borderRadius = model.stack
    ? 2
    : model.isHorizontal
      ? [0, 3, 3, 0]
      : [3, 3, 0, 0];
  return {
    type: 'bar',
    id: series.id,
    name: series.name,
    [model.isHorizontal ? 'xAxisIndex' : 'yAxisIndex']: series.axisIndex,
    ...(model.stack ? { stack: STACK } : {}),
    data: toDrawnValues({ series, model }).map((value, index) =>
      isDeselected({ series, model, index })
        ? { value, itemStyle: { color: theme.deselected } }
        : { value },
    ),
    itemStyle: {
      color: style.isBarGradient
        ? toGradient({
            color: series.color,
            from: 1,
            to: BAR_BASE_OPACITY,
            isHorizontal: model.isHorizontal,
          })
        : series.color,
      borderRadius,
    },
    ...(style.hasBarBackground
      ? {
          showBackground: true,
          backgroundStyle: { color: theme.track, borderRadius },
        }
      : {}),
    label: dataLabels
      ? {
          show: true,
          position: model.isHorizontal ? 'right' : 'top',
          ...toValueLabelStyle(theme),
          formatter: ({ dataIndex, value }) =>
            step && dataIndex % step !== 0
              ? ''
              : formatValueLabel({
                  metricId: series.metricId,
                  value,
                  formatMetric,
                }),
        }
      : undefined,
    emphasis: { focus: 'series' },
  };
};

const toLineSeriesOption = ({
  series,
  model,
}: {
  series: StudioCharts.Series;
  model: StudioCharts.CartesianModel;
}): LineSeriesOption => {
  // The metric's lines follow the curve, comparisons too; only its own
  // values are filled. Averages and trends stay straight.
  const style =
    series.role === 'value' || series.role === 'comparison'
      ? model.style ?? {}
      : {};
  const fill = series.role === 'value' ? style.lineFill : undefined;
  return {
    type: 'line',
    id: series.id,
    name: series.name,
    [model.isHorizontal ? 'xAxisIndex' : 'yAxisIndex']: series.axisIndex,
    data: series.values,
    color: series.color,
    ...(style.curve === 'smooth' ? { smooth: true } : {}),
    // Each value held until the next, as Highcharts' step: 'left'
    ...(style.curve === 'step' ? { step: 'end' as const } : {}),
    ...(fill
      ? {
          areaStyle:
            fill === 'gradient'
              ? {
                  color: toGradient({
                    color: series.color,
                    from: LINE_FILL_OPACITY,
                    to: 0,
                  }),
                }
              : { color: series.color, opacity: SOLID_FILL_OPACITY },
        }
      : {}),
    showSymbol: false,
    symbol: 'circle',
    symbolSize: 6,
    // The line itself reports the pointer (it has no points to), so hovering
    // it highlights it, and a Composite's Number with it
    triggerEvent: 'line',
    lineStyle: {
      width: series.role === 'value' ? 3 : 2,
      type: series.role === 'value' ? 'solid' : 'dashed',
      cap: 'round',
      join: 'round',
    },
    // As the Highcharts lines: bolder on hover (3px to 5px), the other series
    // faded
    emphasis: {
      focus: 'series',
      lineStyle: { width: series.role === 'value' ? 5 : 3 },
    },
  };
};

// Under every series (bars are 2), over the grid lines (0)
const SHADOW_Z = 1;

// As Highcharts fills an area by default
const AREA_FILL_OPACITY = 0.75;

// Faded as an estimate, as the Highcharts forecasts are: thinner, fainter
// and dashed
const FORECAST_LINE_OPACITY = 0.7;
const FORECAST_BAND_OPACITY = 0.3;

// A forecast: its dashed line on through the periods ahead, and, where its
// range is drawn, the band between each period's low and high (a hidden
// low line with the range stacked on it). Neither fades while another
// series is hovered. The band's series follow every other series (see
// toCartesianOption), so the chart's own series keep their positions.
const toForecastSeriesOptions = ({
  series,
  model,
}: {
  series: StudioCharts.Series;
  model: StudioCharts.CartesianModel;
}): { line: LineSeriesOption; band: LineSeriesOption[] } => {
  const axis = {
    [model.isHorizontal ? 'xAxisIndex' : 'yAxisIndex']: series.axisIndex,
  };
  const line: LineSeriesOption = {
    type: 'line',
    id: series.id,
    name: series.name,
    ...axis,
    data: series.values,
    color: series.color,
    showSymbol: false,
    symbol: 'circle',
    symbolSize: 6,
    z: 3,
    lineStyle: {
      width: 2,
      type: 'dashed',
      opacity: FORECAST_LINE_OPACITY,
      cap: 'round',
    },
    emphasis: { disabled: true },
    blur: { lineStyle: { opacity: FORECAST_LINE_OPACITY } },
  };
  const { ranges } = series;
  if (!ranges) {
    return { line, band: [] };
  }
  const stack = `${series.id}:range`;
  const bandPart = {
    type: 'line' as const,
    ...axis,
    stack,
    // Stacked as they are, even where the low is below zero
    stackStrategy: 'all' as const,
    color: series.color,
    // No point is marked, even under the pointer
    symbol: 'none',
    silent: true,
    z: 1,
    lineStyle: { opacity: 0 },
    emphasis: { disabled: true },
  };
  return {
    line,
    band: [
      {
        ...bandPart,
        id: `${series.id}:low`,
        name: `${series.name} low`,
        data: ranges.map((range) => (range ? range[0] : null)),
      },
      {
        ...bandPart,
        id: `${series.id}:high`,
        name: `${series.name} range`,
        data: ranges.map((range) => (range ? range[1] - range[0] : null)),
        areaStyle: { color: series.color, opacity: FORECAST_BAND_OPACITY },
        blur: { areaStyle: { opacity: FORECAST_BAND_OPACITY } },
      },
    ],
  };
};

// The forecast's periods shaded, from the last actual category to the last
// predicted one (half a category further on a bar chart, to cover its
// bar), after a faint line where they start
const toForecastPeriodMarks = ({
  model,
  theme,
}: {
  model: StudioCharts.CartesianModel;
  theme: StudioCharts.Theme;
}): Pick<LineSeriesOption, 'markArea' | 'markLine'> => {
  const { forecastPeriod } = model;
  if (!forecastPeriod) {
    return {};
  }
  const axis = model.isHorizontal ? 'yAxis' : 'xAxis';
  const hasBars = model.series.some((s) => s.kind === 'bar');
  return {
    markArea: {
      silent: true,
      itemStyle: { color: theme.forecastPeriod },
      data: [
        [
          { [axis]: forecastPeriod.start },
          { [axis]: forecastPeriod.end + (hasBars ? 0.5 : 0) },
        ],
      ],
    },
    markLine: {
      silent: true,
      symbol: ['none', 'none'],
      label: { show: false },
      lineStyle: { color: theme.forecastStart, type: 'dashed', width: 1 },
      data: [{ [axis]: forecastPeriod.start }],
    },
  };
};

// A forecast point's 95% range, for the tooltip. None where the forecast
// joins the last actual value, or its range is hidden.
const toRangeNote = ({
  series,
  dataIndex,
  formatMetric,
}: {
  series: StudioCharts.Series;
  dataIndex: number;
  formatMetric: FormatMetric;
}) => {
  const range = series.ranges ? series.ranges[dataIndex] : null;
  if (!range) {
    return undefined;
  }
  const [low, high] = range.map((value) =>
    formatMetric({ metricId: series.metricId, value }),
  );
  return `95% range: ${low} – ${high}`;
};

// A stacked area: its line filled down to the series below, solid (as the
// Highcharts area's own fill) or fading where the block asks
const toAreaSeriesOption = ({
  series,
  model,
}: {
  series: StudioCharts.Series;
  model: StudioCharts.CartesianModel;
}): LineSeriesOption => {
  const style = model.style ?? {};
  return {
    type: 'line',
    id: series.id,
    name: series.name,
    [model.isHorizontal ? 'xAxisIndex' : 'yAxisIndex']: series.axisIndex,
    stack: STACK,
    data: toDrawnValues({ series, model }),
    color: series.color,
    ...(style.curve === 'smooth' ? { smooth: true } : {}),
    ...(style.curve === 'step' ? { step: 'end' as const } : {}),
    areaStyle:
      style.lineFill === 'gradient'
        ? {
            color: toGradient({
              color: series.color,
              from: LINE_FILL_OPACITY,
              to: 0,
            }),
          }
        : { color: series.color, opacity: AREA_FILL_OPACITY },
    showSymbol: false,
    symbol: 'circle',
    symbolSize: 6,
    triggerEvent: 'line',
    lineStyle: { width: 3, cap: 'round', join: 'round' },
    emphasis: { focus: 'series', lineStyle: { width: 5 } },
  };
};

const toTooltip = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.CartesianModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): EChartsOption['tooltip'] => ({
  ...toTooltipStyle(theme),
  trigger: 'axis',
  // A bar chart's band behind its bars, as Highcharts draws it: ECharts
  // puts it over the series (z 50), which its opaque colour would hide
  axisPointer: model.series.some((s) => s.kind === 'bar')
    ? {
        type: 'shadow',
        z: SHADOW_Z,
        shadowStyle: { color: theme.crosshair },
      }
    : { type: 'line', lineStyle: { color: theme.crosshair } },
  formatter: (params) => {
    const points = Array.isArray(params) ? params : [params];
    if (points.length === 0) {
      return '';
    }
    const category = model.categories[points[0].dataIndex];
    return toTooltipHtml({
      title: category ? category.title : '',
      // The chart's series with a value here: not a forecast's band, nor
      // where the forecast joins the last actual value (already listed)
      // A stack lists only the terms with a value in the category.
      rows: points.flatMap((point) => {
        const series = model.series.find((s) => s.id === point.seriesId);
        // As the model has it: a percentage stack draws shares
        const raw = series ? series.values[point.dataIndex] : undefined;
        if (
          !series ||
          raw === null ||
          raw === undefined ||
          (model.stack && raw === 0) ||
          (series.role === 'forecast' &&
            series.values.findIndex((v) => v !== null) === point.dataIndex)
        ) {
          return [];
        }
        return [
          {
            marker: typeof point.marker === 'string' ? point.marker : '',
            name: point.seriesName ?? '',
            value: formatMetric({
              metricId: series.metricId,
              value: raw as number | null,
            }),
            note: toRangeNote({
              series,
              dataIndex: point.dataIndex,
              formatMetric,
            }),
          },
        ];
      }),
    });
  },
});

// The ECharts options a bar, horizontal bar, combo or line chart draws with
const toCartesianOption = ({
  model,
  theme,
  formatMetric,
  labelLayout,
  tickCount,
}: {
  model: StudioCharts.CartesianModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
  // How the category labels fit (see getAxisLabelLayout); absent as the
  // model has them
  labelLayout?: AxisLabelLayout;
  // The value axis's intervals (see getValueTickCount)
  tickCount?: number;
}): EChartsOption => {
  const categoryAxis = toCategoryAxis({
    model,
    theme,
    labelLayout: labelLayout ?? {
      isRotated: false,
      step: model.axisLabelStep,
    },
  });
  const valueAxes = toValueAxes({ model, theme, tickCount });
  // A Pictorial Bar's bars are icons; their background track is faint icons
  // on a hidden copy of the category axis (see toPictorialTrackOptions)
  const style = model.style ?? {};
  const { pictorial } = style;
  const tracks =
    pictorial && style.hasBarBackground
      ? toPictorialTrackOptions({ model, pictorial, trackColor: theme.track })
      : [];
  const categoryAxes =
    tracks.length > 0
      ? [categoryAxis, { ...categoryAxis, show: false }]
      : [categoryAxis];
  // Each forecast's line and band, by its series' id. The first carries the
  // forecast period's marks.
  const forecasts = new Map(
    model.series
      .filter((s) => s.role === 'forecast')
      .map((series, index) => {
        const { line, band } = toForecastSeriesOptions({ series, model });
        return [
          series.id,
          {
            line:
              index === 0
                ? { ...line, ...toForecastPeriodMarks({ model, theme }) }
                : line,
            band,
          },
        ];
      }),
  );
  const legendSeries = model.series.filter((s) => s.role !== 'forecast');
  return {
    ...toBaseOption(theme),
    grid: {
      left: 8,
      right: 12,
      top: 24,
      bottom: model.showLegend ? 32 : 8,
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel',
    },
    // A forecast continues its metric's line: its metric's legend entry
    // stands for both
    legend: toLegend({
      show: model.showLegend,
      theme,
      names: legendSeries.map((s) => s.name),
      // A Pictorial Bar's bars keyed by their icons; its lines keep a dot
      icons: pictorial
        ? legendSeries.map((s) =>
            s.kind === 'bar' && s.role === 'value'
              ? toIconSymbol(getSeriesIcon({ pictorial, series: s }))
              : 'circle',
          )
        : undefined,
    }),
    tooltip: toTooltip({ model, theme, formatMetric }),
    xAxis: (model.isHorizontal
      ? valueAxes
      : categoryAxes) as XAXisComponentOption[],
    yAxis: (model.isHorizontal
      ? categoryAxes
      : valueAxes) as YAXisComponentOption[],
    series: [
      ...tracks,
      ...model.series.map(
        (
          series,
        ): BarSeriesOption | LineSeriesOption | PictorialBarSeriesOption => {
          if (series.kind === 'bar') {
            const bar = toBarSeriesOption({
              series,
              model,
              theme,
              formatMetric,
            });
            return pictorial && series.role === 'value'
              ? toPictorialSeriesOption({ bar, series, model, pictorial })
              : bar;
          }
          if (series.kind === 'area') {
            return toAreaSeriesOption({ series, model });
          }
          const forecast = forecasts.get(series.id);
          return forecast
            ? forecast.line
            : toLineSeriesOption({ series, model });
        },
      ),
      ...Array.from(forecasts.values()).flatMap((forecast) => forecast.band),
    ],
  };
};

export default toCartesianOption;
